import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { CoercedSelectValue } from '../../prop-types';
import QuantityButton from './button';

const handleInputSelect = (e) => {
  e.preventDefault();
};

const handleInputFocus = (e, value) => {
  if (value === 0) {
    e.target.select();
  }
};

const handleDecrementClick = (onChange, currentValue) => {
  onChange({
    target: {
      value: (parseInt(currentValue, 10) || 0) - 1,
    },
  });
};

const handleIncrementClick = (onChange, currentValue) => {
  onChange({
    target: {
      value: (parseInt(currentValue, 10) || 0) + 1,
    },
  });
};

/**
 * A controlled number input field with internal state to provide a handle updating input display value and external state.
 *
 * - The component's value is controlled by the `value` prop.
 * - When a user types in the input, the internal `displayValue` is updated, but `onChange` is not called immediately.
 * - The value is "committed" to the parent via `onChange` only when the input is blurred or the 'Enter' key is pressed.
 * - When "committing", the value sent to the parent is the raw number the user typed - this may be clamped by the parent so we
 *    will update again if `propValue` changes.
 * - Pressing the 'Escape' key will revert the input to the last known `propValue` and blur the input without calling `onChange`.
 * - If the input is empty on blur, it will revert to the last known `propValue`.
 * - When increment or decrement buttons are enabled and clicked, `onChange` will be called with the next value.
 */
const NumberInputField = (props) => {
  const {
    noButtons,
    label,
    disabled,
    value: propValue,
    id,
    min,
    max,
    onChange,
    inputClassName,
    describedBy,
  } = props;

  const [displayValue, setDisplayValue] = useState(String(propValue));
  const isFocused = useRef(false);
  const isEscaping = useRef(false);

  // This effect runs whenever propValue changes.
  // It will update displayValue to match propValue, but only if the input is not focused.
  // This prevents overwriting user input while they are typing. The exception is when hitting
  // 'Enter' or blurring, which will update the value as it may have been clamped by the parent.
  useEffect(() => {
    if (!isFocused.current) {
      setDisplayValue(String(propValue));
    }
  }, [propValue]);

  // Validates and commits the current input value to the parent.
  const commitValue = () => {
    const parsedDisplayValue = parseInt(displayValue, 10);

    let valueToReportToParent;
    if (displayValue === '' || isNaN(parsedDisplayValue)) {
      // If input is empty or not a number, revert to the last valid value.
      valueToReportToParent = propValue;
    } else {
      // Otherwise, report the user's entered value.
      valueToReportToParent = parsedDisplayValue;
    }

    // Report the value to the parent. The parent is responsible for clamping.
    onChange({
      target: {
        value: valueToReportToParent,
      },
    });

    // If input is empty or not a number, revert to the last valid value.
    if (displayValue === '' || isNaN(parsedDisplayValue)) {
      setDisplayValue(String(propValue));
      return;
    }

    // Clamp the value for display, anticipating the parent's clamping.
    const clampedValue = Math.max(min, Math.min(max, parsedDisplayValue));
    setDisplayValue(String(clampedValue));
  };

  const handleInputChange = (e) => {
    // Always update local display value immediately when user types.
    setDisplayValue(e.target.value);
  };

  const handleInputFocusInternal = (e) => {
    isFocused.current = true;
    handleInputFocus(e, propValue);
  };

  const handleInputBlur = () => {
    if (!isFocused.current) return;

    isFocused.current = false;
    if (isEscaping.current) {
      isEscaping.current = false;
      return; // don't update parent if this blur is from the Escape reset sequence
    }
    commitValue();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Manually call blur handler and then blur input to commit value and lose focus
      handleInputBlur();
      e.target.blur();
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      isEscaping.current = true;
      setDisplayValue(String(propValue));
      e.target.blur();
    }
  };

  return (
    <div
      className={classNames('input-group number-field flex-nowrap', {
        'no-buttons': noButtons,
      })}>
      {!noButtons && (
        <span className="input-group-prepend">
          <QuantityButton
            ariaLabel={`${label} print, ${propValue}, decrease quantity`}
            className="less"
            disabled={disabled || propValue <= min}
            inputID={id}
            onClick={() => handleDecrementClick(onChange, propValue)}>
            <i className="fa fa-minus" aria-hidden="true" />
          </QuantityButton>
        </span>
      )}
      <input
        id={id}
        aria-label={label}
        aria-describedby={describedBy || undefined}
        aria-valuemax={max}
        aria-valuemin={min}
        aria-valuenow={propValue !== '' ? propValue : undefined}
        aria-valuetext={propValue === '' ? 'blank' : undefined}
        className={inputClassName}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleInputFocusInternal}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        onSelect={(e) => handleInputSelect(e)}
        disabled={disabled}
      />
      {!noButtons && (
        <span className="input-group-append">
          <QuantityButton
            ariaLabel={`${label} print, ${propValue}, increase quantity`}
            className="more"
            disabled={disabled || propValue >= max}
            inputID={id}
            onClick={() => handleIncrementClick(onChange, propValue)}>
            <i className="fa fa-plus" aria-hidden="true" />
          </QuantityButton>
        </span>
      )}
    </div>
  );
};

NumberInputField.propTypes = {
  noButtons: PropTypes.bool,
  label: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  value: CoercedSelectValue.isRequired,
  id: PropTypes.string.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  inputClassName: PropTypes.string,
  describedBy: PropTypes.string,
};

NumberInputField.defaultProps = {
  noButtons: false,
  label: undefined,
  describedBy: undefined,
};

export default NumberInputField;
